import { Head, Link, router } from "@inertiajs/react"
import {
  ArrowDownCircle,
  ArrowUpCircle,
  DollarSign,
  Landmark,
  TrendingDown,
  TrendingUp,
} from "lucide-react"
import { useState } from "react"
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from "recharts"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/app-layout"
import { formatCurrency, formatDate } from "@/lib/utils"
import { dashboardPath, entriesPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface TrendPoint {
  month: string
  revenue: number
  expenses: number
}

interface IncomeProjectionPoint {
  month: string
  actual: number | null
  projected: number | null
}

interface RecentEntry {
  id: number
  description: string
  date: string
  direction: "expense" | "revenue" | "transfer" | "tax"
  status: string
  amount: number
  category: { id: number; name: string; color: string } | null
}

interface CategoryExpense {
  name: string
  amount: number
  color: string
}

interface DashboardProps {
  revenue: number
  expenses: number
  net_profit: number
  taxes_paid: number
  trend: TrendPoint[]
  income_projection: IncomeProjectionPoint[] | null
  expense_by_category: CategoryExpense[]
  recent_entries: RecentEntry[]
  needs_review_count: number
  tags: string[]
  tag: string
  start_date: string
  end_date: string
}

const DATE_PRESETS = [
  { label: "This Month", start: firstOfMonth(0), end: endOfMonth(0) },
  { label: "Last Month", start: firstOfMonth(-1), end: endOfMonth(-1) },
  ...[1, 2, 3, 4].map((quarter) => ({
    label: `Q${quarter}`,
    start: firstOfQuarter(quarter),
    end: endOfQuarter(quarter),
  })),
  { label: "This Year", start: firstOfYear(0), end: endOfYear(0) },
  { label: "Last Year", start: firstOfYear(-1), end: endOfYear(-1) },
]

function firstOfMonth(offset: number) {
  const d = new Date()
  d.setMonth(d.getMonth() + offset, 1)
  return d.toISOString().split("T")[0]
}

function endOfMonth(offset: number) {
  const d = new Date()
  d.setMonth(d.getMonth() + offset + 1, 0)
  return d.toISOString().split("T")[0]
}

function firstOfQuarter(quarter: number) {
  const year = new Date().getFullYear()
  const month = (quarter - 1) * 3 + 1
  return `${year}-${String(month).padStart(2, "0")}-01`
}

function endOfQuarter(quarter: number) {
  const year = new Date().getFullYear()
  const date = new Date(year, quarter * 3, 0)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
}

function firstOfYear(offset: number) {
  return `${new Date().getFullYear() + offset}-01-01`
}

function endOfYear(offset: number) {
  return `${new Date().getFullYear() + offset}-12-31`
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Dashboard", href: dashboardPath() },
]

const chartConfig = {
  revenue: { label: "Revenue", color: "#22c55e" },
  expenses: { label: "Expenses", color: "#ef4444" },
  projected: { label: "Cumulative income", color: "#0ea5e9" },
} satisfies ChartConfig

export default function Dashboard({
  revenue,
  expenses,
  net_profit,
  taxes_paid,
  trend,
  income_projection,
  expense_by_category,
  recent_entries,
  needs_review_count,
  tags,
  tag,
  start_date,
  end_date,
}: DashboardProps) {
  const [showCustom, setShowCustom] = useState(false)
  const [customStart, setCustomStart] = useState(start_date)
  const [customEnd, setCustomEnd] = useState(end_date)
  const chartTrend = trend.map((point, index) => ({
    ...point,
    income_projection:
      income_projection?.[index]?.actual ??
      income_projection?.[index]?.projected ??
      null,
  }))
  const projectedYearEnd =
    income_projection?.[income_projection.length - 1]?.projected

  function applyPreset(start: string, end: string) {
    setShowCustom(false)
    const query: Record<string, string> = { start_date: start, end_date: end }
    if (tag) query.tag = tag
    router.get(dashboardPath(), query, { preserveState: true })
  }

  function applyCustom() {
    if (customStart && customEnd) {
      const query: Record<string, string> = {
        start_date: customStart,
        end_date: customEnd,
      }
      if (tag) query.tag = tag
      router.get(dashboardPath(), query, { preserveState: true })
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Dashboard" />

      <div className="flex flex-col gap-6 p-4">
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {DATE_PRESETS.map((preset) => (
              <Button
                key={preset.label}
                variant={
                  start_date === preset.start && end_date === preset.end
                    ? "default"
                    : "outline"
                }
                size="sm"
                onClick={() => applyPreset(preset.start, preset.end)}
              >
                {preset.label}
              </Button>
            ))}
            <Button
              variant={showCustom ? "default" : "outline"}
              size="sm"
              onClick={() => setShowCustom(!showCustom)}
            >
              Custom
            </Button>
            <Select
              value={tag || "__all__"}
              onValueChange={(value) => {
                const query: Record<string, string> = {
                  start_date,
                  end_date,
                }
                if (value !== "__all__") query.tag = value
                router.get(dashboardPath(), query, { preserveState: true })
              }}
            >
              <SelectTrigger className="w-[210px]" size="sm">
                <SelectValue placeholder="Filter by tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All tags</SelectItem>
                {tags.map((tagName) => (
                  <SelectItem key={tagName} value={tagName}>
                    {tagName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {showCustom && (
            <div className="flex items-end gap-3">
              <div className="grid gap-1">
                <Label className="text-xs">From</Label>
                <Input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                />
              </div>
              <div className="grid gap-1">
                <Label className="text-xs">To</Label>
                <Input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                />
              </div>
              <Button size="sm" onClick={applyCustom}>
                Apply
              </Button>
            </div>
          )}
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Revenue</CardTitle>
              <TrendingUp className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(revenue)}
              </div>
              <p className="text-muted-foreground text-xs">
                {formatDate(start_date)} to {formatDate(end_date)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Expenses</CardTitle>
              <TrendingDown className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {formatCurrency(expenses)}
              </div>
              <p className="text-muted-foreground text-xs">
                {formatDate(start_date)} to {formatDate(end_date)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Net Profit</CardTitle>
              <DollarSign className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div
                className={`text-2xl font-bold ${net_profit >= 0 ? "text-green-600" : "text-red-600"}`}
              >
                {formatCurrency(net_profit)}
              </div>
              <p className="text-muted-foreground text-xs">
                {formatDate(start_date)} to {formatDate(end_date)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Taxes Paid</CardTitle>
              <Landmark className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">
                {formatCurrency(taxes_paid)}
              </div>
              <p className="text-muted-foreground text-xs">
                {formatDate(start_date)} to {formatDate(end_date)}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">
                Revenue vs Expenses
              </CardTitle>
              <p className="text-muted-foreground text-xs">
                {DATE_PRESETS.find(
                  (p) => p.start === start_date && p.end === end_date,
                )?.label ??
                  `${formatDate(start_date)} to ${formatDate(end_date)}`}
                {projectedYearEnd != null && (
                  <span className="ml-2">
                    · Year-end income estimate:{" "}
                    {formatCurrency(projectedYearEnd)}
                  </span>
                )}
              </p>
            </CardHeader>
            <CardContent>
              {trend.every((t) => t.revenue === 0 && t.expenses === 0) ? (
                <p className="text-muted-foreground py-8 text-center text-sm">
                  Add entries to see your revenue vs expense trend.
                </p>
              ) : (
                <ChartContainer
                  config={chartConfig}
                  className="h-[200px] w-full"
                >
                  <ComposedChart data={chartTrend}>
                    <CartesianGrid vertical={false} />
                    <YAxis yAxisId="amount" hide />
                    {income_projection && (
                      <YAxis
                        yAxisId="projection"
                        orientation="right"
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => formatCurrency(Number(value))}
                        width={76}
                      />
                    )}
                    <XAxis
                      dataKey="month"
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                    />
                    {income_projection && <Legend />}
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar
                      dataKey="revenue"
                      fill="var(--color-revenue)"
                      radius={4}
                      yAxisId="amount"
                    />
                    <Bar
                      dataKey="expenses"
                      fill="var(--color-expenses)"
                      radius={4}
                      yAxisId="amount"
                    />
                    {income_projection && (
                      <Line
                        type="monotone"
                        dataKey="income_projection"
                        name="Cumulative income"
                        stroke="var(--color-projected)"
                        strokeDasharray="5 5"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                        yAxisId="projection"
                      />
                    )}
                  </ComposedChart>
                </ChartContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">
                Expenses by Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              {expense_by_category.length === 0 ? (
                <p className="text-muted-foreground py-8 text-center text-sm">
                  No expenses in this period.
                </p>
              ) : (
                <div className="flex flex-col items-center gap-4 sm:flex-row">
                  <ChartContainer
                    config={{}}
                    className="h-[200px] w-[200px] shrink-0"
                  >
                    <PieChart>
                      <Pie
                        data={expense_by_category}
                        dataKey="amount"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                      >
                        {expense_by_category.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <ChartTooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null
                          const data = payload[0].payload as CategoryExpense
                          return (
                            <div className="bg-background border-border rounded-md border px-3 py-2 shadow-sm">
                              <div className="flex items-center gap-2">
                                <span
                                  className="h-2.5 w-2.5 rounded-full"
                                  style={{ backgroundColor: data.color }}
                                />
                                <span className="text-sm font-medium">
                                  {data.name}
                                </span>
                              </div>
                              <div className="text-muted-foreground mt-1 text-sm">
                                {formatCurrency(data.amount)}
                              </div>
                            </div>
                          )
                        }}
                      />
                    </PieChart>
                  </ChartContainer>
                  <div className="space-y-2">
                    {expense_by_category.map((cat) => (
                      <div
                        key={cat.name}
                        className="flex items-center justify-between gap-4"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block h-3 w-3 rounded-full"
                            style={{ backgroundColor: cat.color }}
                          />
                          <span className="text-sm">{cat.name}</span>
                        </div>
                        <span className="font-mono text-sm text-red-600">
                          {formatCurrency(cat.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium">
              Recent Entries
            </CardTitle>
            {needs_review_count > 0 && (
              <Link href={entriesPath({ status: "needs_review" })}>
                <Badge variant="outline" className="text-amber-600">
                  {needs_review_count} need review
                </Badge>
              </Link>
            )}
          </CardHeader>
          <CardContent>
            {recent_entries.length === 0 ? (
              <p className="text-muted-foreground py-4 text-center text-sm">
                No entries yet. Add your first expense to get started.
              </p>
            ) : (
              <div className="space-y-3">
                {recent_entries.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      {entry.direction === "revenue" ? (
                        <ArrowUpCircle className="h-4 w-4 text-green-500" />
                      ) : entry.direction === "tax" ? (
                        <ArrowDownCircle className="h-4 w-4 text-amber-500" />
                      ) : entry.direction === "transfer" ? (
                        <ArrowDownCircle className="h-4 w-4 text-blue-500" />
                      ) : (
                        <ArrowDownCircle className="h-4 w-4 text-red-500" />
                      )}
                      <div>
                        <p className="text-sm font-medium">
                          {entry.description}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {formatDate(entry.date)}
                          {entry.category && (
                            <span className="ml-2 inline-flex items-center gap-1">
                              <span
                                className="inline-block h-2 w-2 rounded-full"
                                style={{
                                  backgroundColor: entry.category.color,
                                }}
                              />
                              {entry.category.name}
                            </span>
                          )}
                          {entry.status === "needs_review" && (
                            <Badge
                              variant="outline"
                              className="ml-2 text-xs text-amber-600"
                            >
                              Needs Review
                            </Badge>
                          )}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`font-mono text-sm font-medium ${
                        entry.direction === "revenue"
                          ? "text-green-600"
                          : entry.direction === "tax"
                            ? "text-amber-600"
                            : entry.direction === "transfer"
                              ? "text-blue-600"
                              : "text-red-600"
                      }`}
                    >
                      {entry.direction === "revenue" ? "+" : "-"}
                      {formatCurrency(entry.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
